import React, { useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import classNames from 'classnames';
import { Close as CloseIcon } from '@vedmarket/ui-kit';
import { Chat as ChatIcon } from '@vedmarket/ui-kit';
import { Send as SendIcon } from '@vedmarket/ui-kit';
import { Check, Favorite, UploadFile } from '@vedmarket/ui-kit';
import CircularProgress from '@mui/material/CircularProgress';

import { API_URL, CHAT_API_URL } from 'config/constants';
import {getAcceptLanguage} from 'config/i18';
import './realtime-support-chat.scss';

type RealtimeSupportMessage = {
  id: string;
  role: 'user' | 'operator' | 'system';
  content: string;
  createdAt?: string;
  deliveryStatus?: 'sent' | 'delivered' | 'read';
  authorName?: string;
  managerId?: number;
  attachments?: RealtimeSupportAttachment[];
};

type RealtimeSupportAttachment = {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  isImage: boolean;
  previewUrl?: string;
  remoteUrl?: string;
  remoteId?: string | number;
};

type PendingAttachment = RealtimeSupportAttachment & {
  file: File;
  uploadProgress: number;
  uploadStatus: 'idle' | 'uploading' | 'uploaded' | 'error';
  error?: string;
};

type RealtimeSupportChatProps = {
  socketUrl: string;
  clientId: string;
  token?: string;
  lang?: string;
  socketPath?: string;
  title?: string;
  placeholder?: string;
  className?: string;
  initiallyOpen?: boolean;
  connectOnMount?: boolean;
  historyUrl?: string;
  mapIncomingMessage?(payload: any): RealtimeSupportMessage | null;
};

const MOCK_OPERATOR_NAME = 'Павел';
const EMOJI_LIST = [ '😀', '😁', '😂', '😊', '😍', '🤝', '👍', '🔥', '🎉', '🙏', '❤️', '🤔' ];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_ATTACHMENTS_COUNT = 3;
const SOCKET_EVENT_PRESENCE_UPDATE = 'support_presence_update';
const SUPPORT_EMAIL = 'support@vedmarket.ru';
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];

const defaultIncomingMessageMapper = (payload: any): RealtimeSupportMessage | null => {
  let roleFromPayload: RealtimeSupportMessage['role'] = 'operator';
  if (payload?.type === 'user') {
    roleFromPayload = 'user';
  } else if (payload?.type === 'system') {
    roleFromPayload = 'system';
  }
  const content = payload?.content;
  const attachments = Array.isArray(payload?.attachments) ? payload.attachments : [];
  const normalizedAttachments = attachments
    .map((item: any, index: number) => {
      const rawUrl = item?.url || item?.remoteUrl || item?.image || item?.file;
      if (typeof rawUrl !== 'string' || !rawUrl) return null;

      const mimeType = item?.mimeType || item?.type || '';
      return {
        id: item?.id ? String(item.id) : `${Date.now()}-${index}`,
        name: item?.name || 'Файл',
        size: Number(item?.size || 0),
        mimeType,
        isImage: Boolean(mimeType?.startsWith?.('image/')),
        previewUrl: rawUrl,
      };
    })
    .filter(Boolean) as RealtimeSupportAttachment[];

  if ((typeof content !== 'string' || !content.trim()) && normalizedAttachments.length === 0) return null;

  return {
    id: payload?.id ? String(payload.id) : `${Date.now()}-${Math.random()}`,
    role: roleFromPayload,
    content: typeof content === 'string' ? content : '',
    createdAt: payload?.created_at || payload?.createdAt,
    authorName: roleFromPayload === 'operator' ? payload?.operator_name || MOCK_OPERATOR_NAME : undefined,
    managerId: typeof payload?.manager_id === 'number' ? payload.manager_id : undefined,
    attachments: normalizedAttachments.length ? normalizedAttachments : undefined,
  };
};

const parseServerDate = (value: string) => {
  const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(value);
  const normalized = hasTimezone ? value : `${value}Z`;
  return new Date(normalized);
};

const formatMessageTime = (value?: string) => {
  if (!value) return '';

  const date = parseServerDate(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getDeliveryStatusLabel = (status?: RealtimeSupportMessage['deliveryStatus']) => {
  if (status === 'read') return 'прочитано';
  if (status === 'delivered') return 'доставлено';
  return 'отправлено';
};

const URL_REGEXP = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

const renderMessageWithLinks = (content: string, linkClassName: string) => {
  const parts = content.split(URL_REGEXP);

  return parts.map((part, index) => {
    if (!part) return null;
    const isUrl = /^(https?:\/\/|www\.)/i.test(part);
    if (!isUrl) {
      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    }

    const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
    return (
      <a
        key={`link-${index}`}
        href={href}
        target='_blank'
        rel='noreferrer'
        className={linkClassName}
      >
        {part}
      </a>
    );
  });
};

const RealtimeSupportChat = ({
  socketUrl,
  clientId,
  token,
  lang,
  socketPath = '/ws/socket.io',
  title = 'Чат поддержки',
  placeholder = 'Введите сообщение...',
  className,
  initiallyOpen = false,
  connectOnMount = true,
  historyUrl,
  mapIncomingMessage = defaultIncomingMessageMapper,
}: RealtimeSupportChatProps) => {
  const [ isOpen, setIsOpen ] = useState(initiallyOpen);
  const [ isConnected, setIsConnected ] = useState(false);
  const [ messages, setMessages ] = useState<RealtimeSupportMessage[]>([]);
  const [ text, setText ] = useState('');
  const [ isOperatorTyping, setIsOperatorTyping ] = useState(false);
  const [ pendingAttachments, setPendingAttachments ] = useState<PendingAttachment[]>([]);
  const [ isEmojiPickerOpen, setIsEmojiPickerOpen ] = useState(false);
  const [ lightboxImage, setLightboxImage ] = useState<{ src: string; alt: string } | null>(null);
  const [ isManagerOnline, setIsManagerOnline ] = useState<boolean>(true);

  const socketRef = useRef<Socket | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingOperatorMessageRef = useRef('');
  const updateLastUserMessageStatus = (status: RealtimeSupportMessage['deliveryStatus']) => {
    setMessages((prev) => {
      const updated = [ ...prev ];
      for (let i = updated.length - 1; i >= 0; i -= 1) {
        if (updated[i].role === 'user') {
          updated[i] = { ...updated[i], deliveryStatus: status };
          break;
        }
      }
      return updated;
    });
  };


  const normalizedClientId = useMemo(() => clientId.trim(), [ clientId ]);

  useEffect(() => {
    if (!connectOnMount || !normalizedClientId) return;

    socketRef.current = io(socketUrl, {
      path: socketPath,
      query: { client_id: normalizedClientId },
      // iOS Safari is more stable with polling fallback before websocket upgrade.
      transports: [ 'polling', 'websocket' ],
      closeOnBeforeunload: true,
      addTrailingSlash: false,
      auth: token ? { token } : undefined,
    });

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    const onPresenceUpdate = (payload: any) => {
      if (typeof payload?.is_online === 'boolean') {
        setIsManagerOnline(payload.is_online);
      }
    };
    const onMessage = (payload: any) => {
      if (payload?.type === 'chunk') {
        setIsOperatorTyping(true);
        updateLastUserMessageStatus('delivered');

        if (typeof payload?.content === 'string') {
          pendingOperatorMessageRef.current += payload.content;
        }

        if (payload?.stop === true) {
          const finalContent = pendingOperatorMessageRef.current.trim();
          pendingOperatorMessageRef.current = '';

          if (finalContent) {
            setMessages((prev) => [
              ...prev,
              {
                id: `${Date.now()}-${Math.random()}`,
                role: 'operator',
                content: finalContent,
                createdAt: new Date().toISOString(),
                authorName: MOCK_OPERATOR_NAME,
              },
            ]);
          }

          setIsOperatorTyping(false);
          updateLastUserMessageStatus('read');
        }

        return;
      }

      const mapped = mapIncomingMessage(payload);
      if (!mapped) return;

      // Prevent duplicate echo of locally added user message.
      if (mapped.role === 'user') return;

      if (mapped.role === 'operator') {
        setIsOperatorTyping(false);
        updateLastUserMessageStatus('read');
      }

      setMessages((prev) => [ ...prev, mapped ]);
    };

    socketRef.current.on('connect', onConnect);
    socketRef.current.on('disconnect', onDisconnect);
    socketRef.current.on('message', onMessage);
    socketRef.current.on(SOCKET_EVENT_PRESENCE_UPDATE, onPresenceUpdate);

    if (historyUrl) {
      fetch(historyUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
        .then((res) => res.json())
        .then((data) => {
          let incoming: unknown[] = [];
          if (Array.isArray(data)) {
            incoming = data;
          } else if (Array.isArray(data?.messages)) {
            incoming = data.messages;
          }
          const prepared = incoming
            .map((item) => mapIncomingMessage(item))
            .filter(Boolean) as RealtimeSupportMessage[];
          setMessages(prepared);
        })
        .catch(() => {
          // Ignore history errors, live chat should still work.
        });
    }

    fetch(`${CHAT_API_URL}/api/chat-api/v1/support/presence/`)
      .then((res) => res.json())
      .then((data) => {
        if (typeof data?.is_online === 'boolean') {
          setIsManagerOnline(data.is_online);
        }
      })
      .catch(() => {
        // Keep default state if endpoint unavailable.
      });

    return () => {
      pendingOperatorMessageRef.current = '';
      setIsOperatorTyping(false);
      socketRef.current?.off('connect', onConnect);
      socketRef.current?.off('disconnect', onDisconnect);
      socketRef.current?.off('message', onMessage);
      socketRef.current?.off(SOCKET_EVENT_PRESENCE_UPDATE, onPresenceUpdate);
      socketRef.current?.disconnect();
    };
  }, [ connectOnMount, normalizedClientId, socketPath, socketUrl, token, historyUrl, mapIncomingMessage ]);

  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [ messages, isOpen ]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLightboxImage(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    return () => {
      pendingAttachments.forEach((attachment) => {
        if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
      });
    };
  }, []);

  const isAttachmentAllowed = (file: File) => {
    return ALLOWED_MIME_TYPES.includes(file.type);
  };

  const handleAttachmentSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (!files.length) return;
    const availableSlots = Math.max(0, MAX_ATTACHMENTS_COUNT - pendingAttachments.length);
    if (availableSlots === 0) {
      event.target.value = '';
      return;
    }

    const selectedFiles = files.slice(0, availableSlots);

    const nextAttachments: PendingAttachment[] = selectedFiles.map((file) => {
      const isImage = file.type.startsWith('image/');
      const isAllowed = isAttachmentAllowed(file);
      const isTooLarge = file.size > MAX_FILE_SIZE_BYTES;
      let attachmentError: string | undefined;

      if (!isAllowed) {
        attachmentError = 'Неподдерживаемый формат';
      } else if (isTooLarge) {
        attachmentError = 'Файл больше 10 MB';
      }

      return {
        id: `${Date.now()}-${file.name}-${Math.random()}`,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        isImage,
        file,
        uploadProgress: 0,
        uploadStatus: isAllowed && !isTooLarge ? 'idle' : 'error',
        error: attachmentError,
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
      };
    });

    setPendingAttachments((prev) => [ ...prev, ...nextAttachments ]);
    event.target.value = '';
  };

  const handleRemovePendingAttachment = (id: string) => {
    setPendingAttachments((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((item) => item.id !== id);
    });
  };

  const uploadAttachment = (attachment: PendingAttachment): Promise<PendingAttachment> => {
    if (attachment.uploadStatus === 'uploaded') {
      return Promise.resolve(attachment);
    }

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('image', attachment.file);
      formData.append('file', attachment.file);
      formData.append('title', attachment.name);

      xhr.open('POST', `${API_URL}/media_storage/images/`);
      if (token) {
        xhr.setRequestHeader('authorization', `Bearer ${token}`);
      }
      xhr.setRequestHeader('Accept-Language', getAcceptLanguage(lang));

      xhr.upload.onprogress = (progressEvent) => {
        const value = progressEvent.total > 0
          ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
          : 0;
        setPendingAttachments((prev) =>
          prev.map((item) => (item.id === attachment.id
            ? { ...item, uploadStatus: 'uploading' as const, uploadProgress: value }
            : item)),
        );
      };

      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          const failed = { ...attachment, uploadStatus: 'error' as const, error: 'Ошибка загрузки файла' };
          resolve(failed);
          return;
        }

        let responseData: any = {};
        try {
          responseData = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch (error) {
          responseData = {};
        }

        const uploaded = {
          ...attachment,
          uploadStatus: 'uploaded' as const,
          uploadProgress: 100,
          remoteId: responseData?.id || responseData?.uuid,
          remoteUrl: responseData?.image || responseData?.url || responseData?.file,
          error: undefined,
        };

        resolve(uploaded);
      };

      xhr.onerror = () => {
        resolve({ ...attachment, uploadStatus: 'error', error: 'Ошибка сети при загрузке' });
      };

      xhr.send(formData);
    });
  };

  const uploadPendingAttachments = async () => {
    const target = pendingAttachments.filter((item) => item.uploadStatus !== 'error');
    const results: PendingAttachment[] = [];

    for (const attachment of target) {
      // eslint-disable-next-line no-await-in-loop
      const uploaded = await uploadAttachment(attachment);
      results.push(uploaded);
      setPendingAttachments((prev) =>
        prev.map((item) => (item.id === uploaded.id ? uploaded : item)),
      );
    }

    return results;
  };

  const handlePickEmoji = (emoji: string) => {
    setText((prev) => `${prev}${emoji}`);
    setIsEmojiPickerOpen(false);
    inputRef.current?.focus();
  };

  const sendMessage = () => {
    if (!isManagerOnline) return;

    const trimmedText = text.trim();
    const hasValidAttachments = pendingAttachments.some((item) => item.uploadStatus !== 'error');
    if (!trimmedText && !hasValidAttachments) return;
    if (pendingAttachments.some((item) => item.uploadStatus === 'uploading')) return;

    (async () => {
      const uploadedAttachments = await uploadPendingAttachments();
      const successfulAttachments = uploadedAttachments.filter((item) => item.uploadStatus === 'uploaded');

      if (!trimmedText && successfulAttachments.length === 0) return;

      const nextMessage: RealtimeSupportMessage = {
        id: `${Date.now()}-${Math.random()}`,
        role: 'user',
        content: trimmedText,
        createdAt: new Date().toISOString(),
        deliveryStatus: 'sent',
        attachments: successfulAttachments.map((item) => ({
          id: item.id,
          name: item.name,
          size: item.size,
          mimeType: item.mimeType,
          isImage: item.isImage,
          previewUrl: item.remoteUrl || item.previewUrl,
          remoteUrl: item.remoteUrl,
          remoteId: item.remoteId,
        })),
      };

      setMessages((prev) => [ ...prev, nextMessage ]);
      pendingOperatorMessageRef.current = '';
      socketRef.current?.emit('support_message', {
        content: nextMessage.content,
        attachments: successfulAttachments.map((item) => ({
          id: item.remoteId,
          url: item.remoteUrl,
          name: item.name,
          size: item.size,
          mimeType: item.mimeType,
        })),
      });

      pendingAttachments.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      setPendingAttachments([]);
      setText('');
      setIsEmojiPickerOpen(false);
      inputRef.current?.focus();
    })();
  };

  return (
    <div className={classNames('realtime-support-chat', className)}>
      {!isOpen && (
        <button
          type='button'
          className='realtime-support-chat__launcher'
          onClick={() => setIsOpen(true)}
          aria-label='Открыть чат'
        >
          <ChatIcon fontSize='small' />
        </button>
      )}

      {isOpen && (
        <div className='realtime-support-chat__panel'>
          <div className='realtime-support-chat__header'>
            <div className='realtime-support-chat__title'>{title}</div>
            <button
              type='button'
              className='realtime-support-chat__close'
              onClick={() => setIsOpen(false)}
              aria-label='Закрыть чат'
            >
              <CloseIcon fontSize='inherit' />
            </button>
            <div className='realtime-support-chat__status'>
              <span
                className={classNames('realtime-support-chat__dot', {
                  'realtime-support-chat__dot_online': isConnected && isManagerOnline,
                })}
              />
              {isManagerOnline ? 'Менеджер онлайн' : 'Менеджер оффлайн'}
            </div>
          </div>

          <div ref={messagesRef} className='realtime-support-chat__messages'>
            <div className='realtime-support-chat__brand'>Мессенджер Вэдмаркет</div>
            {messages.length === 0 && (
              <div className='realtime-support-chat__empty'>
                Напишите сообщение, и оператор ответит в этом окне.
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={classNames('realtime-support-chat__bubble', {
                  'realtime-support-chat__bubble_user': message.role === 'user',
                  'realtime-support-chat__bubble_operator': message.role === 'operator',
                  'realtime-support-chat__bubble_system': message.role === 'system',
                })}
              >
                {message.role === 'operator' && (
                  <span className='realtime-support-chat__author'>
                    {message.authorName || MOCK_OPERATOR_NAME}
                  </span>
                )}
                {Boolean(message.content) && (
                  <span>
                    {renderMessageWithLinks(message.content, 'realtime-support-chat__messageLink')}
                  </span>
                )}
                {!message.content && Array.isArray(message.attachments) && message.attachments.length > 0 && (
                  <span className='realtime-support-chat__attachmentOnlyText'>Вложение</span>
                )}
                {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                  <div className='realtime-support-chat__attachments'>
                    {message.attachments.map((attachment) => (
                      <div key={attachment.id} className='realtime-support-chat__attachmentItem'>
                        {attachment.isImage && attachment.previewUrl ? (
                          <img
                            src={attachment.previewUrl}
                            alt={attachment.name}
                            className='realtime-support-chat__attachmentImage'
                            onClick={() => setLightboxImage({ src: attachment.previewUrl || '', alt: attachment.name })}
                          />
                        ) : (
                          <span className='realtime-support-chat__attachmentIcon'>📎</span>
                        )}
                        <span className='realtime-support-chat__attachmentName'>{attachment.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                {message.role !== 'system' && (
                  <span className='realtime-support-chat__meta'>
                    {formatMessageTime(message.createdAt)}
                    {message.role === 'user' && (
                      <span className='realtime-support-chat__statusText'>
                        {getDeliveryStatusLabel(message.deliveryStatus)}
                      </span>
                    )}
                    {message.role === 'user' && (
                      <Check
                        fontSize='inherit'
                        className={classNames({
                          'realtime-support-chat__statusIcon_read': message.deliveryStatus === 'read',
                          'realtime-support-chat__statusIcon_delivered': message.deliveryStatus === 'delivered',
                        })}
                      />
                    )}
                  </span>
                )}
              </div>
            ))}

            {isOperatorTyping && (
              <div className='realtime-support-chat__typing'>
                <span>{MOCK_OPERATOR_NAME} печатает</span>
                <span className='realtime-support-chat__typingDots' aria-hidden='true'>
                  <span className='realtime-support-chat__typingDot' />
                  <span className='realtime-support-chat__typingDot' />
                  <span className='realtime-support-chat__typingDot' />
                </span>
              </div>
            )}
          </div>

          {pendingAttachments.length > 0 && (
            <div className='realtime-support-chat__pendingAttachments'>
              {pendingAttachments.map((attachment) => (
                <div key={attachment.id} className='realtime-support-chat__pendingAttachment'>
                  {attachment.isImage && attachment.previewUrl ? (
                    <img
                      src={attachment.previewUrl}
                      alt={attachment.name}
                      className='realtime-support-chat__pendingAttachmentImage'
                      onClick={() => setLightboxImage({ src: attachment.previewUrl || '', alt: attachment.name })}
                    />
                  ) : (
                    <span className='realtime-support-chat__attachmentIcon'>📎</span>
                  )}
                  <span className='realtime-support-chat__pendingAttachmentName'>{attachment.name}</span>
                  {attachment.uploadStatus === 'uploading' && (
                    <span className='realtime-support-chat__pendingAttachmentProgress'>{attachment.uploadProgress}%</span>
                  )}
                  {attachment.uploadStatus === 'error' && (
                    <span className='realtime-support-chat__pendingAttachmentError'>{attachment.error}</span>
                  )}
                  <button
                    type='button'
                    className='realtime-support-chat__pendingAttachmentRemove'
                    onClick={() => handleRemovePendingAttachment(attachment.id)}
                    aria-label='Удалить вложение'
                  >
                    <CloseIcon fontSize='inherit' />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className='realtime-support-chat__input'>
            <textarea
              ref={inputRef}
              className='realtime-support-chat__textarea'
              value={isManagerOnline
                ? text
                : 'Менеджеры сейчас недоступны'}
              onChange={(event) => {
                if (!isManagerOnline) return;
                setText(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={placeholder}
              disabled={!isManagerOnline}
            />
            <button
              type='button'
              className='realtime-support-chat__send'
              onClick={sendMessage}
              disabled={
                !isManagerOnline
                || ((!text.trim() && pendingAttachments.length === 0)
                || pendingAttachments.some((item) => item.uploadStatus === 'uploading'))
              }
              aria-label='Отправить сообщение'
            >
              {pendingAttachments.some((item) => item.uploadStatus === 'uploading') ? (
                <CircularProgress size={16} />
              ) : (
                <SendIcon fontSize='small' />
              )}
            </button>
          </div>
          {!isManagerOnline && (
            <div className='realtime-support-chat__offlineHint'>
              Свяжитесь с нами: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </div>
          )}
          {isEmojiPickerOpen && (
            <div className='realtime-support-chat__emojiPicker'>
              {EMOJI_LIST.map((emoji) => (
                <button
                  type='button'
                  key={emoji}
                  className='realtime-support-chat__emojiButton'
                  onClick={() => handlePickEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
          <div className='realtime-support-chat__toolbar'>
            <button
              type='button'
              className='realtime-support-chat__toolButton'
              aria-label='Прикрепить файл'
              onClick={() => fileInputRef.current?.click()}
              disabled={!isManagerOnline || pendingAttachments.length >= MAX_ATTACHMENTS_COUNT}
            >
              <UploadFile fontSize='inherit' />
            </button>
            <button
              type='button'
              className='realtime-support-chat__toolButton'
              aria-label='Эмодзи'
              onClick={() => setIsEmojiPickerOpen((prev) => !prev)}
              disabled={!isManagerOnline}
            >
              <Favorite fontSize='inherit' />
            </button>
          </div>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            className='realtime-support-chat__fileInput'
            onChange={handleAttachmentSelect}
          />
        </div>
      )}
      {lightboxImage && (
        <div
          className='realtime-support-chat__lightbox'
          onClick={() => setLightboxImage(null)}
          role='presentation'
        >
          <button
            type='button'
            className='realtime-support-chat__lightboxClose'
            onClick={() => setLightboxImage(null)}
            aria-label='Закрыть предпросмотр'
          >
            <CloseIcon fontSize='inherit' />
          </button>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            className='realtime-support-chat__lightboxImage'
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default RealtimeSupportChat;
export type { RealtimeSupportChatProps, RealtimeSupportMessage };
